import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, IconButton, Stack, TextField, Tooltip } from '@mui/material';
import ColorizeOutlinedIcon from '@mui/icons-material/ColorizeOutlined';
import { FP } from './lib/ui';

const clamp01 = (value) => Math.min(1, Math.max(0, value));

const supportsEyeDropper =
  typeof window !== 'undefined' && typeof window.EyeDropper === 'function';

/** Accepts `fff`, `#fff`, `ffffff`, `#ffffff` → `#FFFFFF`, else null. */
export const normalizeHexInput = (value) => {
  const raw = String(value ?? '')
    .trim()
    .replace(/^#/, '');
  if (/^[0-9a-fA-F]{3}$/.test(raw)) {
    return `#${raw
      .split('')
      .map((char) => char + char)
      .join('')}`.toUpperCase();
  }
  if (/^[0-9a-fA-F]{6}$/.test(raw)) {
    return `#${raw.toUpperCase()}`;
  }
  return null;
};

const hexToRgb = (hex) => {
  const normalized = normalizeHexInput(hex) || '#000000';
  return {
    r: parseInt(normalized.slice(1, 3), 16),
    g: parseInt(normalized.slice(3, 5), 16),
    b: parseInt(normalized.slice(5, 7), 16),
  };
};

const rgbToHex = ({ r, g, b }) =>
  `#${[r, g, b]
    .map((channel) =>
      Math.round(Math.min(255, Math.max(0, channel)))
        .toString(16)
        .padStart(2, '0')
    )
    .join('')}`.toUpperCase();

const rgbToHsv = ({ r, g, b }) => {
  const red = r / 255;
  const green = g / 255;
  const blue = b / 255;
  const max = Math.max(red, green, blue);
  const min = Math.min(red, green, blue);
  const delta = max - min;

  let hue = 0;
  if (delta !== 0) {
    if (max === red) {
      hue = ((green - blue) / delta) % 6;
    } else if (max === green) {
      hue = (blue - red) / delta + 2;
    } else {
      hue = (red - green) / delta + 4;
    }
    hue *= 60;
    if (hue < 0) {
      hue += 360;
    }
  }
  return { h: hue, s: max === 0 ? 0 : delta / max, v: max };
};

const hsvToRgb = ({ h, s, v }) => {
  const chroma = v * s;
  const secondary = chroma * (1 - Math.abs(((h / 60) % 2) - 1));
  const match = v - chroma;
  let rgb = [0, 0, 0];
  if (h < 60) rgb = [chroma, secondary, 0];
  else if (h < 120) rgb = [secondary, chroma, 0];
  else if (h < 180) rgb = [0, chroma, secondary];
  else if (h < 240) rgb = [0, secondary, chroma];
  else if (h < 300) rgb = [secondary, 0, chroma];
  else rgb = [chroma, 0, secondary];
  return {
    r: (rgb[0] + match) * 255,
    g: (rgb[1] + match) * 255,
    b: (rgb[2] + match) * 255,
  };
};

const hsvToHex = (hsv) => rgbToHex(hsvToRgb(hsv));

const handleSx = {
  position: 'absolute',
  width: 20,
  height: 20,
  borderRadius: '50%',
  border: '3px solid #FFFFFF',
  boxShadow: '0 0 0 1px rgba(15,23,42,0.25), 0 2px 6px rgba(15,23,42,0.30)',
  transform: 'translate(-50%, -50%)',
  pointerEvents: 'none',
};

const surfaceSx = {
  position: 'relative',
  touchAction: 'none',
  outline: 'none',
  '&:focus-visible': {
    boxShadow: `0 0 0 3px rgba(45,127,249,0.35)`,
  },
};

/**
 * Dependency-free hex colour picker: saturation/value area + hue slider + hex
 * field. `onChange` fires continuously while dragging; `onDragStart` lets the
 * caller take a single undo snapshot before the stream begins.
 */
const ColorPicker = ({ value, onChange, onDragStart }) => {
  const [hsv, setHsv] = useState(() => rgbToHsv(hexToRgb(value)));
  const [hexText, setHexText] = useState(
    () => normalizeHexInput(value) || '#FFFFFF'
  );

  const hsvRef = useRef(hsv);
  const lastEmittedRef = useRef(normalizeHexInput(value));
  const onChangeRef = useRef(onChange);

  useEffect(() => {
    hsvRef.current = hsv;
  }, [hsv]);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // Follow external changes (preset swatch, section switch) without fighting
  // the value we just emitted — that would reset hue on greys/blacks.
  useEffect(() => {
    const next = normalizeHexInput(value);
    if (!next || next === lastEmittedRef.current) {
      return;
    }
    lastEmittedRef.current = next;
    setHsv(rgbToHsv(hexToRgb(next)));
    setHexText(next);
  }, [value]);

  const emit = useCallback((nextHsv) => {
    setHsv(nextHsv);
    hsvRef.current = nextHsv;
    const hex = hsvToHex(nextHsv);
    lastEmittedRef.current = hex;
    setHexText(hex);
    onChangeRef.current?.(hex);
  }, []);

  const dragTypeRef = useRef(null);

  const applyPointer = useCallback(
    (event, type, node) => {
      if (!node) {
        return;
      }
      const rect = node.getBoundingClientRect();
      if (type === 'sv') {
        emit({
          ...hsvRef.current,
          s: clamp01((event.clientX - rect.left) / rect.width),
          v: 1 - clamp01((event.clientY - rect.top) / rect.height),
        });
        return;
      }
      emit({
        ...hsvRef.current,
        h: clamp01((event.clientX - rect.left) / rect.width) * 360,
      });
    },
    [emit]
  );

  const startDrag = (event, type) => {
    event.preventDefault();
    dragTypeRef.current = type;
    onDragStart?.();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    applyPointer(event, type, event.currentTarget);
  };

  const continueDrag = (event, type) => {
    if (dragTypeRef.current !== type) {
      return;
    }
    applyPointer(event, type, event.currentTarget);
  };

  const endDrag = (event) => {
    dragTypeRef.current = null;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
  };

  const nudge = (event, type) => {
    const step = event.shiftKey ? 0.1 : 0.02;
    const horizontal =
      (event.key === 'ArrowRight' ? 1 : 0) -
      (event.key === 'ArrowLeft' ? 1 : 0);
    const vertical =
      (event.key === 'ArrowUp' ? 1 : 0) - (event.key === 'ArrowDown' ? 1 : 0);
    if (!horizontal && !vertical) {
      return;
    }
    event.preventDefault();
    onDragStart?.();
    const current = hsvRef.current;
    if (type === 'sv') {
      emit({
        ...current,
        s: clamp01(current.s + horizontal * step),
        v: clamp01(current.v + vertical * step),
      });
      return;
    }
    emit({
      ...current,
      h: (((current.h + horizontal * step * 360) % 360) + 360) % 360,
    });
  };

  const hueHex = hsvToHex({ h: hsv.h, s: 1, v: 1 });
  const currentHex = hsvToHex(hsv);

  const commitHexText = (raw) => {
    const normalized = normalizeHexInput(raw);
    if (!normalized) {
      return;
    }
    lastEmittedRef.current = normalized;
    const nextHsv = rgbToHsv(hexToRgb(normalized));
    setHsv(nextHsv);
    hsvRef.current = nextHsv;
    setHexText(normalized);
    onChangeRef.current?.(normalized);
  };

  const pickFromScreen = async () => {
    if (!supportsEyeDropper) {
      return;
    }
    try {
      const result = await new window.EyeDropper().open();
      onDragStart?.();
      commitHexText(result?.sRGBHex);
    } catch {
      // The user dismissed the eyedropper — nothing to apply.
    }
  };

  return (
    <Stack spacing={1.5}>
      <Box
        tabIndex={0}
        role="slider"
        aria-label="Saturation and brightness"
        aria-valuetext={currentHex}
        onKeyDown={(event) => nudge(event, 'sv')}
        onPointerDown={(event) => startDrag(event, 'sv')}
        onPointerMove={(event) => continueDrag(event, 'sv')}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        sx={{
          ...surfaceSx,
          height: 176,
          borderRadius: 2,
          cursor: 'crosshair',
          backgroundColor: hueHex,
          border: `1px solid ${FP.line}`,
          backgroundImage:
            'linear-gradient(to top, #000000, rgba(0,0,0,0)), linear-gradient(to right, #FFFFFF, rgba(255,255,255,0))',
        }}
      >
        <Box
          sx={{
            ...handleSx,
            left: `${hsv.s * 100}%`,
            top: `${(1 - hsv.v) * 100}%`,
            bgcolor: currentHex,
          }}
        />
      </Box>

      <Box
        tabIndex={0}
        role="slider"
        aria-label="Hue"
        aria-valuenow={Math.round(hsv.h)}
        aria-valuemin={0}
        aria-valuemax={360}
        onKeyDown={(event) => nudge(event, 'hue')}
        onPointerDown={(event) => startDrag(event, 'hue')}
        onPointerMove={(event) => continueDrag(event, 'hue')}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        sx={{
          ...surfaceSx,
          height: 16,
          mx: '10px',
          borderRadius: 999,
          border: `1px solid ${FP.line}`,
          cursor: 'pointer',
          backgroundImage:
            'linear-gradient(to right, #FF0000, #FFFF00, #00FF00, #00FFFF, #0000FF, #FF00FF, #FF0000)',
        }}
      >
        <Box
          sx={{
            ...handleSx,
            left: `${(hsv.h / 360) * 100}%`,
            top: '50%',
            bgcolor: hueHex,
          }}
        />
      </Box>

      <Stack direction="row" spacing={1} alignItems="center">
        <Box
          sx={{
            width: 44,
            height: 40,
            borderRadius: 2,
            flexShrink: 0,
            bgcolor: currentHex,
            border: `1px solid ${FP.line}`,
            boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.55)',
          }}
        />
        <TextField
          size="small"
          value={hexText}
          placeholder="#RRGGBB"
          onFocus={() => onDragStart?.()}
          onChange={(event) => {
            setHexText(event.target.value);
            commitHexText(event.target.value);
          }}
          onBlur={() => setHexText(currentHex)}
          inputProps={{ spellCheck: false, maxLength: 7 }}
          sx={{
            flex: 1,
            '& .MuiOutlinedInput-root': { height: 40, borderRadius: 2 },
            '& input': {
              fontWeight: 700,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              fontFamily:
                'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
            },
          }}
        />
        {supportsEyeDropper ? (
          <Tooltip title="Pick a color from the screen">
            <IconButton
              onClick={pickFromScreen}
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                border: `1px solid ${FP.line}`,
                color: FP.sub,
                '&:hover': { borderColor: FP.primary, color: FP.primary },
              }}
            >
              <ColorizeOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : null}
      </Stack>
    </Stack>
  );
};

export default ColorPicker;
